import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

/**
 * AutocompleteInput — Input avec dropdown de suggestions filtrées.
 *   items     : Array<any>  — la liste source
 *   getLabel  : (item) => string — texte principal de l'option
 *   getSubtitle: (item) => string — texte secondaire (optionnel)
 *   value     : string
 *   onChange  : (str) => void — texte saisi (contrôlé)
 *   onSelect  : (item) => void — appelé à la sélection d'une option existante
 *   minChars  : number (défaut 2)
 *   placeholder, dataTestId, className : passthrough
 */
export default function AutocompleteInput({
  items = [],
  getLabel = (x) => x?.name || "",
  getSubtitle = () => "",
  value = "",
  onChange,
  onSelect,
  minChars = 2,
  placeholder,
  dataTestId,
  className = "",
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const suggestions = (() => {
    const v = (value || "").trim().toLowerCase();
    if (v.length < minChars) return [];
    return items
      .filter((it) => {
        const lbl = getLabel(it).toLowerCase();
        return lbl.includes(v);
      })
      .slice(0, 10);
  })();

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <Input
        value={value}
        onChange={(e) => { onChange?.(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        data-testid={dataTestId}
        {...rest}
      />
      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-50 top-full left-0 right-0 mt-1 rounded-md border bg-popover shadow-md max-h-64 overflow-auto"
          data-testid={dataTestId ? `${dataTestId}-suggestions` : undefined}
        >
          {suggestions.map((it, i) => (
            <li key={it.id || it._id || i}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onSelect?.(it); onChange?.(getLabel(it)); setOpen(false); }}
                className="w-full text-left px-3 py-2 hover:bg-muted"
                data-testid={dataTestId ? `${dataTestId}-item-${i}` : undefined}
              >
                <div className="text-sm truncate">{getLabel(it)}</div>
                {getSubtitle(it) && <div className="text-[11px] text-muted-foreground truncate">{getSubtitle(it)}</div>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
